import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../hooks/useTheme';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { Panel } from 'primereact/panel';

const TerminosPage = () => {
    const { theme, toggleTheme } = useTheme();

    return (
        <div className={`landing-page theme-${theme}`}>
            <Helmet>
                <title>Términos y Condiciones – Laptops.pe</title>
                <meta name="description" content="Términos y Condiciones de Uso de Laptops.pe, operado por Digital Wins. Última actualización: Octubre 2025." />
                <link rel="canonical" href={`${process.env.REACT_APP_SITE_URL || ''}/terminos`} />
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <Header theme={theme} onToggleTheme={toggleTheme} />

            <main className="landing-main">
                <div className=" py-6">
                    <div className="text-center mb-5">
                        <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-2">
                            <span role="img" aria-label="documento">📜</span> Términos y Condiciones de Uso – Laptops.pe
                        </h1>
                        <p className="text-700">Última actualización: <strong>Octubre 2025</strong></p>
                    </div>

                    <Card className="mb-4">
                        <div className="p-4">
                            <p className="text-700 line-height-3">
                                Bienvenido a <strong>Laptops.pe</strong>, una plataforma de comercio electrónico operada por <strong>Digital Wins</strong>, empresa peruana dedicada a la venta y distribución de productos tecnológicos en todo el país.
                                Al acceder o utilizar este sitio web, usted acepta los presentes <strong>Términos y Condiciones</strong>. Si no está de acuerdo con alguna de las disposiciones, le recomendamos no utilizar nuestros servicios.
                            </p>
                        </div>
                    </Card>

                    <Card className="mb-4">
                        <div className="p-4">
                            <div className="flex align-items-center gap-2 mb-3">
                                <i className="pi pi-id-card text-primary text-2xl" aria-hidden="true"></i>
                                <h2 className="text-2xl font-bold m-0">1. Identificación del titular</h2>
                            </div>
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <Panel header="Razón comercial">
                                        <p className="m-0 text-700">Digital Wins</p>
                                    </Panel>
                                </div>
                                <div className="col-12 md:col-6">
                                    <Panel header="RUC">
                                        <p className="m-0 text-700">20612859630</p>
                                    </Panel>
                                </div>
                                <div className="col-12 md:col-6">
                                    <Panel header="Nombre comercial">
                                        <p className="m-0 text-700">Laptops.pe</p>
                                    </Panel>
                                </div>
                                <div className="col-12 md:col-6">
                                    <Panel header="Correo de contacto">
                                        <p className="m-0 text-700"><a href="mailto:soporte@laptops.pe">soporte@laptops.pe</a></p>
                                    </Panel>
                                </div>
                                <div className="col-12 md:col-6">
                                    <Panel header="Teléfono/WhatsApp">
                                        <p className="m-0 text-700">+51 978 326 796</p>
                                    </Panel>
                                </div>
                                <div className="col-12 md:col-6">
                                    <Panel header="Domicilio legal">
                                        <p className="m-0 text-700">Lima, Perú</p>
                                    </Panel>
                                </div>
                            </div>
                        </div>
                    </Card>

                    <Panel header="2. Objeto" className="mb-4">
                        <p className="text-700">
                            El presente documento regula el acceso, uso y contratación de productos a través de <strong>Laptops.pe</strong>, incluyendo el contenido, funcionalidades, precios, promociones y políticas aplicables a los usuarios y clientes del sitio.
                        </p>
                    </Panel>

                    <Panel header="3. Uso del sitio web" className="mb-4">
                        <p className="text-700">El usuario se compromete a:</p>
                        <ul className="list-disc ml-4 text-700">
                            <li>Utilizar el sitio únicamente para fines lícitos y personales.</li>
                            <li>No manipular, alterar o intentar acceder a información o sistemas del sitio.</li>
                            <li>No realizar compras fraudulentas ni con información falsa o de terceros sin autorización.</li>
                        </ul>
                        <p className="text-700 mt-3">
                            <strong>Digital Wins</strong> se reserva el derecho de suspender o bloquear cuentas que infrinjan estas normas sin previo aviso.
                        </p>
                    </Panel>

                    <Panel header="4. Disponibilidad y precios" className="mb-4">
                        <p className="text-700">Todos los productos y precios mostrados en el sitio son <strong>válidos únicamente mientras haya stock disponible</strong>.</p>
                        <p className="text-700">
                            Nos reservamos el derecho de <strong>modificar precios, características o disponibilidad</strong> sin previo aviso, incluso después de una compra, en caso de errores de publicación o variaciones del proveedor.
                        </p>
                        <p className="text-700">
                            En tales casos, <strong>Digital Wins</strong> podrá anular el pedido y realizar el reembolso correspondiente.
                        </p>
                    </Panel>

                    <Panel header="5. Proceso de compra" className="mb-4">
                        <p className="text-700">El proceso de compra incluye:</p>
                        <ol className="list-decimal ml-4 text-700">
                            <li>Selección del producto y registro del cliente.</li>
                            <li>Confirmación del pedido y método de pago.</li>
                            <li>Validación y despacho del pedido.</li>
                        </ol>
                        <p className="text-700 mt-3">
                            El envío del comprobante electrónico (boleta o factura) se realiza a través de <strong>SUNAT</strong>, conforme a la normativa vigente.
                        </p>
                    </Panel>

                    <Panel header="6. Métodos de pago" className="mb-4">
                        <p className="text-700">Aceptamos los siguientes métodos:</p>
                        <ul className="list-disc ml-4 text-700">
                            <li>Tarjetas de crédito y débito (Visa, Mastercard, etc.)</li>
                            <li>Transferencias bancarias</li>
                            <li>Yape / Plin</li>
                            <li>PagoEfectivo</li>
                        </ul>
                        <p className="text-700 mt-3">
                            Todos los pagos se procesan mediante plataformas seguras de terceros. <strong>Digital Wins no almacena información de tarjetas o cuentas bancarias.</strong>
                        </p>
                    </Panel>

                    <Panel header="7. Despachos y entregas" className="mb-4">
                        <p className="text-700">Los envíos se realizan a nivel nacional mediante operadores logísticos externos.</p>
                        <p className="text-700">Los plazos de entrega pueden variar según destino, disponibilidad del producto y validación del pago.</p>
                        <p className="text-700"><strong>Digital Wins no se responsabiliza por retrasos ocasionados por:</strong></p>
                        <ul className="list-disc ml-4 text-700">
                            <li>Errores en los datos de envío proporcionados por el cliente.</li>
                            <li>Factores externos como huelgas, bloqueos, desastres naturales o demoras de transportistas.</li>
                        </ul>
                        <p className="text-700 mt-3">Una vez entregado el pedido al transportista, la <strong>responsabilidad de la entrega final</strong> recae en el operador logístico.</p>
                    </Panel>

                    <Panel header="8. Garantías y devoluciones" className="mb-4">
                        <p className="text-700">
                            Todos los productos comercializados por <strong>Laptops.pe</strong> son <strong>nuevos, originales y cuentan con garantía del fabricante</strong>.
                        </p>
                        <p className="text-700">Las condiciones de garantía son determinadas por cada marca y pueden variar. <strong>Digital Wins actúa como intermediario</strong>, gestionando el soporte entre el cliente y el fabricante cuando corresponda.</p>
                        <p className="text-700">No se aceptan devoluciones por cambio de opinión o incompatibilidad del producto. Solo se aceptarán reclamos en los siguientes casos:</p>
                        <ul className="list-disc ml-4 text-700">
                            <li>Producto con falla de fábrica dentro del plazo de garantía.</li>
                            <li>Error en el modelo o especificación entregada.</li>
                        </ul>
                        <p className="text-700 mt-3">Para solicitar soporte, el cliente debe comunicarse a <a href="mailto:soporte@laptops.pe">soporte@laptops.pe</a> y seguir las instrucciones del área técnica.</p>
                    </Panel>

                    <Panel header="9. Limitación de responsabilidad" className="mb-4">
                        <p className="text-700"><strong>Digital Wins</strong> no será responsable, bajo ninguna circunstancia, por:</p>
                        <ul className="list-disc ml-4 text-700">
                            <li>Daños directos o indirectos ocasionados por el uso del sitio web.</li>
                            <li>Fallas en los servicios de terceros (bancos, couriers, proveedores).</li>
                            <li>Pérdida de datos o información derivada de problemas técnicos o ciberataques.</li>
                        </ul>
                        <p className="text-700 mt-3">El uso del sitio y la compra de productos es bajo responsabilidad exclusiva del usuario.</p>
                    </Panel>

                    <Panel header="10. Propiedad intelectual" className="mb-4">
                        <p className="text-700">
                            Todo el contenido del sitio (imágenes, logos, textos, marcas, código, estructura, etc.) es propiedad de <strong>Digital Wins</strong> o de sus respectivos titulares, y está protegido por las leyes peruanas e internacionales de <strong>propiedad intelectual</strong>.
                        </p>
                        <p className="text-700">Está prohibida su reproducción total o parcial sin autorización expresa.</p>
                    </Panel>

                    <Panel header="11. Protección de datos personales" className="mb-4">
                        <p className="text-700">
                            <strong>Digital Wins</strong> cumple con la <strong>Ley N° 29733 – Ley de Protección de Datos Personales</strong> y su reglamento.
                        </p>
                        <p className="text-700">
                            La información proporcionada por los usuarios se utiliza exclusivamente para la gestión de pedidos, atención y promociones, sin ser compartida con terceros no autorizados.
                        </p>
                        <p className="text-700">Los usuarios pueden ejercer sus derechos de acceso, rectificación o eliminación escribiendo a <a href="mailto:soporte@laptops.pe">soporte@laptops.pe</a>.</p>
                    </Panel>

                    <Panel header="12. Modificaciones" className="mb-4">
                        <p className="text-700">
                            <strong>Digital Wins</strong> se reserva el derecho de modificar estos términos en cualquier momento y sin previo aviso.
                        </p>
                        <p className="text-700">Las actualizaciones se publicarán en esta misma página, y el uso continuado del sitio implicará la aceptación de los cambios.</p>
                    </Panel>

                    <Panel header="13. Jurisdicción y ley aplicable" className="mb-4">
                        <p className="text-700">
                            Este acuerdo se rige por las leyes de la <strong>República del Perú</strong>.
                        </p>
                        <p className="text-700">Cualquier controversia será resuelta ante los tribunales competentes de la ciudad de Lima, renunciando expresamente a cualquier otro fuero o jurisdicción.</p>
                    </Panel>

                    <Panel header="14. Contacto" className="mb-4">
                        <p className="text-700">Si tienes consultas sobre estos términos, puedes comunicarte con nosotros a través de:</p>
                        <ul className="list-none m-0 p-0 text-700">
                            <li>📧 <a href="mailto:soporte@laptops.pe">soporte@laptops.pe</a></li>
                            <li>📱 WhatsApp: +51 978 326 796</li>
                        </ul>
                    </Panel>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default TerminosPage;